/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable jsdoc/newline-after-description */
import { Injectable } from '@angular/core';
import Parse from 'parse';
import { Observable, from } from 'rxjs';
import { map } from 'rxjs/operators';
import { UserType } from './usertype';
import { Classifier } from './dbhc.model';

@Injectable({
  providedIn: 'root'
})
export class ParseStatsService {
  private readonly usersClassName = 'users';

  constructor(
  ) {}

  //==================
  //STATISTICS RELATED FUNCTIONS
  //==================

  /**
   * Count the total number of users
   * @returns Promise with the count of users
   */
  async countUsers(): Promise<number> {
    try {
      const query = new Parse.Query(this.usersClassName);
      return await query.count();
    } catch (error) {
      console.error('Error counting users:', error);
      throw new Error('Failed to count users');
    }
  }

  /**
   * Set the timestamp of the last classification for a user
   * @param uid User ID
   * @returns Promise that resolves when the update is complete
   */
  async setLastClassificationTimestampForUid(uid: string): Promise<void> {
    try {
      // Find the user
      const query = new Parse.Query(this.usersClassName);
      query.equalTo('uid', uid);
      const user = await query.first();

      if (user) {
        // Update the timestamp
        user.set('lastClassificationTimestamp', new Date());
        await user.save();
      } else {
        // Create a new user record if it doesn't exist
        const User = Parse.Object.extend(this.usersClassName);
        const newUser = new User();
        newUser.set('uid', uid);
        newUser.set('lastClassificationTimestamp', new Date());
        await newUser.save();
      }
    } catch (error) {
      console.error('Error setting last classification timestamp:', error);
      throw new Error('Failed to set last classification timestamp');
    }
  }

  /**
   * Get the timestamp of the last classification for a user
   * @param uid User ID
   * @returns Promise with the timestamp or null if not found
   */
  async getLastClassificationTimestampForUid(uid: string): Promise<Date | null> {
    try {
      const query = new Parse.Query(this.usersClassName);
      query.equalTo('uid', uid);
      const user = await query.first();

      if (user) {
        return user.get('lastClassificationTimestamp');
      }
      return null;
    } catch (error) {
      console.error('Error getting last classification timestamp:', error);
      throw new Error('Failed to get last classification timestamp');
    }
  }

  //==================
  //USER RELATED FUNCTIONS
  //==================

  /**
   * Set the display name for a user
   * @param uid User ID
   * @param displayName Display name to set
   * @returns Promise that resolves when the update is complete
   */
  async setFirestoreDisplayName(uid: string, displayName: string): Promise<void> {
    try {
      const query = new Parse.Query(this.usersClassName);
      query.equalTo('uid', uid);
      const user = await query.first();

      if (user) {
        user.set('displayName', displayName);
        await user.save();
      } else {
        const User = Parse.Object.extend(this.usersClassName);
        const newUser = new User();
        newUser.set('uid', uid);
        newUser.set('displayName', displayName);
        await newUser.save();
      }
    } catch (error) {
      console.error('Error setting display name:', error);
      throw new Error('Failed to set display name');
    }
  }

  /**
   * Get the display name for a user
   * @param uid User ID
   * @returns Promise with the display name or null if not found
   */
  async getFirestoreDisplayName(uid: string): Promise<string | null> {
    try {
      const query = new Parse.Query(this.usersClassName);
      query.equalTo('uid', uid);
      const user = await query.first();

      if (user) {
        return user.get('displayName');
      }
      return null;
    } catch (error) {
      console.error('Error getting display name:', error);
      throw new Error('Failed to get display name');
    }
  }

  /**
   * Get the user type for a user, determining it if not already set
   * @param uid User ID
   * @param email User email
   * @returns Promise with the user type
   */
  async getUserType(uid: string, email: string): Promise<UserType> {
    try {
      const userType = await this.getParseUserType(uid);
      if (userType !== null && userType !== undefined) {
        return userType;
      } else {
        return await this.determineUserType(uid, email);
      }
    } catch (error) {
      console.error('Error getting user type:', error);
      throw new Error('Failed to get user type');
    }
  }

  /**
   * Get the user type from Parse
   * @param uid User ID
   * @returns Promise with the user type or null if not found
   */
  async getParseUserType(uid: string): Promise<UserType | null> {
    try {
      const query = new Parse.Query(this.usersClassName);
      query.equalTo('uid', uid);
      const user = await query.first();

      if (user) {
        const userType = user.get('userType');
        if (userType in UserType) {
          return UserType[userType as keyof typeof UserType];
        }
      }
      return null;
    } catch (error) {
      console.error('Error getting user type:', error);
      throw new Error('Failed to get user type');
    }
  }

  /**
   * Get an observable of the user type
   * @param uid User ID
   * @returns Observable of the user type
   */
  getUserTypeObservable(uid: string): Observable<UserType | null> {
    const query = new Parse.Query(this.usersClassName);
    query.equalTo('uid', uid);

    return from(query.first()).pipe(
      map(user => {
        if (!user) {
          return null;
        }

        // Cast user to Parse.Object to access the get method
        const parseUser = user as Parse.Object;
        const userType = parseUser.get('userType');

        if (userType === UserType.Admin) {return UserType.Admin;}
        if (userType === UserType.Demoted) {return UserType.Demoted;}
        if (userType === UserType.Expert) {return UserType.Expert;}
        if (userType === UserType.SuperUser) {return UserType.SuperUser;}
        if (userType === UserType.User) {return UserType.User;}
        return null;
      })
    );
  }

  /**
   * Set the user type for a user
   * @param uid User ID
   * @param userType User type to set
   * @returns Promise that resolves when the update is complete
   */
  async setUserType(uid: string, userType: UserType): Promise<void> {
    try {
      const query = new Parse.Query(this.usersClassName);
      query.equalTo('uid', uid);
      const user = await query.first();

      if (user) {
        user.set('userType', userType);
        await user.save();
      } else {
        const User = Parse.Object.extend(this.usersClassName);
        const newUser = new User();
        newUser.set('uid', uid);
        newUser.set('userType', userType);
        await newUser.save();
      }
    } catch (error) {
      console.error('Error setting user type:', error);
      throw new Error('Failed to set user type');
    }
  }

  /**
   * Determine the user type based on email domain
   * @param uid User ID
   * @param email User email
   * @returns Promise with the determined user type
   */
  async determineUserType(uid: string, email: string): Promise<UserType> {
    try {
      let userType: UserType;

      if (email.endsWith('@blackgem.org')) {
        userType = UserType.Expert;
      } else if (email.endsWith('@ddq.nl')) {
        userType = UserType.Admin;
      } else {
        userType = UserType.User;
      }

      await this.setUserType(uid, userType);
      return userType;
    } catch (error) {
      console.error('Error determining user type:', error);
      throw new Error('Failed to determine user type');
    }
  }

  /**
   * Delete a user from Parse
   * @param uid User ID
   * @returns Promise that resolves when the deletion is complete
   */
  async deleteUser(uid: string): Promise<void> {
    try {
      const query = new Parse.Query(this.usersClassName);
      query.equalTo('uid', uid);
      const user = await query.first();

      if (user) {
        await user.destroy();
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      throw new Error('Failed to delete user');
    }
  }

  /**
   * Get the classification count for a user
   * @param uid User ID
   * @returns Promise with the classification count
   */
  async getUserClassificationCount(uid: string): Promise<number> {
    try {
      const query = new Parse.Query(this.usersClassName);
      query.equalTo('uid', uid);
      const user = await query.first();

      if (user && user.get('classificationCount') !== undefined) {
        return user.get('classificationCount');
      }
      return 0;
    } catch (error) {
      console.error('Error getting user classification count:', error);
      return 0;
    }
  }

  /**
   * Get the monthly classification count for a user
   * @param uid User ID
   * @returns Promise with the monthly classification count
   */
  async getMonthlyUserClassificationCount(uid: string): Promise<number> {
    try {
      const query = new Parse.Query(this.usersClassName);
      query.equalTo('uid', uid);
      const user = await query.first();

      if (user && user.get('monthlyClassificationCount') !== undefined) {
        return user.get('monthlyClassificationCount');
      }
      return 0;
    } catch (error) {
      console.error('Error getting monthly user classification count:', error);
      return 0;
    }
  }

  /**
   * Increment the classification count for a user
   * @param uid User ID
   * @returns Promise that resolves when the update is complete
   */
  async incrementUserClassificationCount(uid: string): Promise<void> {
    try {
      const query = new Parse.Query(this.usersClassName);
      query.equalTo('uid', uid);
      const user = await query.first();

      if (user) {
        user.increment('classificationCount', 1);
        await user.save();
      } else {
        const User = Parse.Object.extend(this.usersClassName);
        const newUser = new User();
        newUser.set('uid', uid);
        newUser.set('classificationCount', 1);
        await newUser.save();
      }
    } catch (error) {
      console.error('Error incrementing user classification count:', error);
      throw new Error('Failed to increment user classification count');
    }
  }

  /**
   * Increment the monthly classification count for a user
   * @param uid User ID
   * @returns Promise that resolves when the update is complete
   */
  async incrementMonthlyUserClassificationCount(uid: string): Promise<void> {
    try {
      const query = new Parse.Query(this.usersClassName);
      query.equalTo('uid', uid);
      const user = await query.first();

      let monthlyClassificationCount = 0;
      if (user) {
        monthlyClassificationCount = user.get('monthlyClassificationCount') || 0;
      }

      const lastClassificationTimestamp = await this.getLastClassificationTimestampForUid(uid);
      const currentTime = new Date();

      const isSameMonth = lastClassificationTimestamp
        ? lastClassificationTimestamp.getMonth() === currentTime.getMonth() &&
          lastClassificationTimestamp.getFullYear() === currentTime.getFullYear()
        : true;

      if (!isSameMonth || monthlyClassificationCount === 0) {
        const startOfMonth = new Date(currentTime.getFullYear(), currentTime.getMonth(), 1);

        const answersQuery = new Parse.Query('answers');
        answersQuery.equalTo('uid', uid);
        answersQuery.greaterThanOrEqualTo('timestamp', startOfMonth);
        const countFromAnswers = await answersQuery.count();

        if (user) {
          user.set('monthlyClassificationCount', countFromAnswers + 1);
          await user.save();
        } else {
          const User = Parse.Object.extend(this.usersClassName);
          const newUser = new User();
          newUser.set('uid', uid);
          newUser.set('monthlyClassificationCount', countFromAnswers + 1);
          await newUser.save();
        }
      } else {
        if (user) {
          user.increment('monthlyClassificationCount', 1);
          await user.save();
        } else {
          const User = Parse.Object.extend(this.usersClassName);
          const newUser = new User();
          newUser.set('uid', uid);
          newUser.set('monthlyClassificationCount', 1);
          await newUser.save();
        }
      }
    } catch (error) {
      console.error('Error incrementing monthly user classification count:', error);
      throw new Error('Failed to increment monthly user classification count');
    }
  }

  //==================
  //RANKING RELATED FUNCTIONS
  //==================

  /**
   * Get the top 10 classifiers from Parse
   * The ranking is pre-calculated by a Python cron job and stored in the 'ranking' class
   * @returns Promise with array of top classifiers
   */
  async getRankingFromParse(): Promise<Classifier[]> {
    try {
      // Query the ranking collection
      const query = new Parse.Query('ranking');
      query.descending('count');
      query.limit(10);

      const results = await query.find();

      // Map the results to Classifier objects
      const topClassifiers = results.map(result => ({
        displayName: result.get('displayName') || 'undefined',
        userType: result.get('userType') || 'User',
        count: result.get('count') || 0,
        imageUrl: result.get('imageUrl') || ''
      }));

      return topClassifiers;
    } catch (error) {
      console.error('Error fetching top 10 classifiers:', error);
      throw error;
    }
  }

  /**
   * Get the current month's ranking from Parse
   * @returns Promise with the current month's classifiers
   */
  async getCurrentMonthRanking(): Promise<Classifier[]> {
    try {
      // Get the current month in YYYY-MM format
      const currentMonth = new Date().toISOString().substring(0, 7);

      // Query the monthlyStats collection for the current month
      const query = new Parse.Query('monthlyStats');
      query.equalTo('month', currentMonth);
      query.descending('count');
      query.limit(10);

      const results = await query.find();

      // Map the results to Classifier objects
      const topClassifiers = results.map(result => ({
          displayName: result.get('displayName') || 'undefined',
          userType: result.get('userType') || 'User',
          count: result.get('count') || 0,
          imageUrl: result.get('imageUrl') || ''
        }));

      return topClassifiers;
    } catch (error) {
      console.error('Error fetching current month ranking:', error);
      throw error;
    }
  }

  /**
   * Get the timestamp of the last ranking update
   * @returns Promise with the timestamp of the last update
   */
  async getLastRankingUpdateTimestamp(): Promise<Date | null> {
    try {
      // Query the rankingMetadata collection
      const query = new Parse.Query('rankingMetadata');
      query.equalTo('key', 'lastUpdate');

      const result = await query.first();

      if (!result) {
        console.error('No ranking metadata found');
        return null;
      }

      // Get the timestamp as a Date object
      const timestamp = result.get('timestamp');

      // Return the Date object directly
      return timestamp instanceof Date ? timestamp : null;
    } catch (error) {
      console.error('Error fetching last ranking update timestamp:', error);
      throw error;
    }
  }

}
